import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import {
  Shield, Radio, MapPin, Camera, Bell, Users, FileText, Eye,
  ChevronRight, Zap, Lock, Wifi, BarChart3, Clock, Smartphone,
  Activity, Target, Layers, ScanFace, UserCheck, Send,
  Cpu, Radar, Globe, Fingerprint, MonitorDot, Crosshair,
  ShieldCheck, Network, BrainCircuit, Satellite
} from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const LOGO_URL = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030788589/ZZQT68UYN6EHt44KWwBWmG/ella_morumbis_logo_5b24d8ff.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] as const }
  })
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i: number) => ({
    opacity: 1,
    transition: { delay: i * 0.1, duration: 0.8 }
  })
};

/* Particle canvas for tech background effect */
function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const particles: { x: number; y: number; vx: number; vy: number; size: number; alpha: number }[] = [];
    const PARTICLE_COUNT = 60;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.4 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(139, 92, 246, ${0.08 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139, 92, 246, ${p.alpha})`;
        ctx.fill();

        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.offsetWidth) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.offsetHeight) p.vy *= -1;
      }

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
}

/* Radar sweep animation */
function RadarSweep() {
  return (
    <div className="absolute right-[8%] top-[18%] w-48 h-48 opacity-15 hidden lg:block">
      <div className="relative w-full h-full">
        {/* Circles */}
        <div className="absolute inset-0 rounded-full border border-purple-500/30" />
        <div className="absolute inset-[25%] rounded-full border border-purple-500/20" />
        <div className="absolute inset-[50%] rounded-full border border-purple-500/10" />
        {/* Sweep */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "conic-gradient(from 0deg, transparent 0deg, rgba(139,92,246,0.3) 30deg, transparent 60deg)",
            animation: "radarSpin 4s linear infinite",
          }}
        />
        {/* Center dot */}
        <div className="absolute inset-[48%] w-[4%] h-[4%] rounded-full bg-purple-400" />
      </div>
    </div>
  );
}

export default function Home() {
  const [, navigate] = useLocation();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#050510] text-white overflow-x-hidden">
      {/* CSS for radar animation */}
      <style>{`
        @keyframes radarSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes scanLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
        .ella-command-gradient {
          background: linear-gradient(135deg, #7c3aed 0%, #a855f7 40%, #ec4899 100%);
        }
        .ella-command-gradient-text {
          background: linear-gradient(135deg, #a78bfa 0%, #c084fc 40%, #f472b6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ella-command-glow {
          box-shadow: 0 0 30px rgba(124,58,237,0.4), 0 0 80px rgba(124,58,237,0.15), 0 0 120px rgba(168,85,247,0.08);
        }
        .glass-panel {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .grid-bg {
          background-image: 
            linear-gradient(rgba(139,92,246,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139,92,246,0.03) 1px, transparent 1px);
          background-size: 60px 60px;
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════ */}
      {/* NAV — ELLA Command Platform                        */}
      {/* ═══════════════════════════════════════════════════ */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrollY > 50 ? "rgba(5,5,16,0.92)" : "rgba(5,5,16,0.4)",
          backdropFilter: "blur(20px)",
          borderBottom: scrollY > 50 ? "1px solid rgba(139,92,246,0.15)" : "1px solid transparent",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg ella-command-gradient flex items-center justify-center ella-command-glow">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                ELLA <span className="text-purple-400/60 text-xs font-normal ml-1">by GAP</span>
              </span>
              <span className="text-[10px] text-purple-300/40 tracking-[0.2em] uppercase font-medium hidden sm:block">
                Command Platform
              </span>
            </div>
          </div>

          {/* Menu */}
          <div className="hidden md:flex items-center gap-1">
            {[
              { label: "Plataforma", href: "#platform" },
              { label: "Dashboard", href: "/dashboard" },
              { label: "Banco Facial", href: "/persons" },
              { label: "Eventos", href: "/events" },
              { label: "Inteligência", href: "#intelligence" },
              { label: "QR Codes", href: "/qr-codes" },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  if (item.href.startsWith("/")) navigate(item.href);
                  else document.querySelector(item.href)?.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-3 py-1.5 text-[13px] text-white/50 hover:text-white/90 transition-colors font-medium tracking-wide"
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <Button
            onClick={() => navigate("/dashboard")}
            className="ella-command-gradient text-white border-0 hover:opacity-90 font-semibold text-sm ella-command-glow"
          >
            Acessar Plataforma
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════ */}
      {/* HERO — Stadium + Holographic Logo                  */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Stadium background image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${LOGO_URL})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: `scale(${1 + scrollY * 0.0003})`,
            transition: "transform 0.1s linear",
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 z-[1]" style={{
          background: "linear-gradient(180deg, rgba(5,5,16,0.75) 0%, rgba(5,5,16,0.88) 50%, rgba(5,5,16,0.98) 100%)",
        }} />
        {/* Purple glow overlay */}
        <div className="absolute inset-0 z-[2]" style={{
          background: "radial-gradient(ellipse at center 30%, rgba(124,58,237,0.12) 0%, transparent 60%)",
        }} />
        {/* Grid background */}
        <div className="absolute inset-0 z-[3] grid-bg" />
        {/* Particles */}
        <div className="absolute inset-0 z-[4]">
          <ParticleCanvas />
        </div>
        {/* Radar */}
        <div className="absolute inset-0 z-[4]">
          <RadarSweep />
        </div>
        {/* Scan line */}
        <div className="absolute inset-0 z-[4] overflow-hidden pointer-events-none">
          <div
            className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent"
            style={{ animation: "scanLine 8s linear infinite" }}
          />
        </div>

        {/* Hero content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center px-6 pt-20">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full glass-panel mb-8">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-sm text-white/60 font-medium tracking-wide">ELLA Command Platform</span>
              <div className="w-px h-3 bg-white/10" />
              <span className="text-xs text-purple-400/80 font-medium">v7.6</span>
            </div>
          </motion.div>

          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight leading-[1.02] mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Monitoramento
            <br />
            <span className="ella-command-gradient-text">Inteligente</span> em
            <br className="hidden sm:block" />
            <span className="ella-command-gradient-text"> Tempo Real</span>
          </motion.h1>

          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="text-lg sm:text-xl text-white/45 max-w-2xl mx-auto mb-12 leading-relaxed font-light"
          >
            Reconhecimento facial, rastreamento de agentes, análise de eventos e comando operacional em uma única plataforma.
          </motion.p>

          <motion.div
            initial="hidden" animate="visible" variants={fadeUp} custom={3}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              size="lg"
              onClick={() => navigate("/dashboard")}
              className="ella-command-gradient text-white border-0 hover:opacity-90 text-base px-10 py-7 font-semibold ella-command-glow rounded-xl"
            >
              <Crosshair className="w-5 h-5 mr-2" />
              Acessar Plataforma
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                document.getElementById("platform")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="text-base px-10 py-7 font-semibold rounded-xl bg-white/[0.03] border-white/10 text-white/70 hover:text-white hover:bg-white/[0.06] hover:border-white/20"
            >
              Ver Demonstração
            </Button>
          </motion.div>

          {/* Stats bar */}
          <motion.div
            initial="hidden" animate="visible" variants={fadeUp} custom={4}
            className="mt-20 glass-panel rounded-2xl p-6 max-w-3xl mx-auto"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {[
                { value: "< 1s", label: "Latência", icon: Zap },
                { value: "GPS", label: "Rastreamento", icon: Satellite },
                { value: "IA", label: "Reconhecimento", icon: BrainCircuit },
                { value: "24/7", label: "Monitoramento", icon: MonitorDot },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <stat.icon className="w-5 h-5 mx-auto mb-2 text-purple-400/60" />
                  <div className="text-xl font-bold ella-command-gradient-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{stat.value}</div>
                  <div className="text-xs text-white/35 mt-1 tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] text-white/25 tracking-[0.3em] uppercase">Scroll</span>
          <div className="w-5 h-8 rounded-full border border-white/15 flex items-start justify-center p-1">
            <motion.div
              className="w-1 h-2 rounded-full bg-purple-400/60"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/* PLATFORM LAYERS                                    */}
      {/* ═══════════════════════════════════════════════════ */}
      <section id="platform" className="py-28 px-6 relative">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-panel mb-6">
              <Layers className="w-3.5 h-3.5 text-purple-400/60" />
              <span className="text-xs text-white/40 tracking-widest uppercase font-medium">Arquitetura</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Camadas da <span className="ella-command-gradient-text">Plataforma</span>
            </h2>
            <p className="text-white/35 max-w-xl mx-auto text-lg font-light">
              Quatro camadas integradas para comando e controle total de operações.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Globe, title: "Camada Visual", desc: "Interface de comando premium com identidade global e experiência imersiva.", tag: "HOME", color: "from-violet-500/20 to-purple-500/20" },
              { icon: MonitorDot, title: "Camada de Operação", desc: "Dashboard executivo com mapa, vídeo ao vivo, eventos e timeline em tempo real.", tag: "DASHBOARD", color: "from-purple-500/20 to-fuchsia-500/20" },
              { icon: BrainCircuit, title: "Camada de Inteligência", desc: "Reconhecimento facial com IA, classificação automática e análise de conflitos.", tag: "IA", color: "from-fuchsia-500/20 to-pink-500/20" },
              { icon: Crosshair, title: "Camada de Execução", desc: "Acionamento de agentes, rastreamento GPS, câmera ao vivo e auto-liberação.", tag: "AGENTES", color: "from-pink-500/20 to-rose-500/20" },
            ].map((layer, i) => (
              <motion.div
                key={layer.title}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="group relative glass-panel rounded-2xl p-6 hover:border-purple-500/20 transition-all duration-500"
              >
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${layer.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-xl ella-command-gradient flex items-center justify-center">
                      <layer.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-[10px] text-purple-400/50 tracking-[0.2em] font-medium">{layer.tag}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-white/90">{layer.title}</h3>
                  <p className="text-sm text-white/35 leading-relaxed">{layer.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/* FEATURES — Operational Capabilities                */}
      {/* ═══════════════════════════════════════════════════ */}
      <section id="intelligence" className="py-28 px-6 relative">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent, rgba(124,58,237,0.03) 50%, transparent)" }} />
        <div className="relative max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-panel mb-6">
              <Cpu className="w-3.5 h-3.5 text-purple-400/60" />
              <span className="text-xs text-white/40 tracking-widest uppercase font-medium">Funcionalidades</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Capacidades <span className="ella-command-gradient-text">Operacionais</span>
            </h2>
            <p className="text-white/35 max-w-xl mx-auto text-lg font-light">
              Tecnologia de ponta para comando e controle com visibilidade total.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Fingerprint, title: "Reconhecimento Facial", desc: "Detecção e identificação em tempo real com banco biométrico, matching automático e geração de eventos." },
              { icon: Satellite, title: "Geolocalização em Tempo Real", desc: "Rastreamento GPS contínuo com trilha de deslocamento, precisão métrica e atualização instantânea." },
              { icon: Camera, title: "Streaming de Vídeo ao Vivo", desc: "Transmissão da câmera do dispositivo e do agente direto para o dashboard de comando." },
              { icon: Send, title: "Acionamento de Agentes", desc: "Despacho automático com rastreamento de status: acionado, em deslocamento, no local, resolvido." },
              { icon: MonitorDot, title: "Dashboard de Comando", desc: "Central executiva com visão unificada de mapa, vídeo, eventos operacionais e timeline." },
              { icon: ShieldCheck, title: "Classificação Inteligente", desc: "Classificação automática: agressor, vítima, monitorado, conflito — com detecção de múltiplas faces." },
            ].map((feat, i) => (
              <motion.div
                key={feat.title}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="group glass-panel rounded-2xl p-6 hover:border-purple-500/15 transition-all duration-500"
              >
                <div className="w-11 h-11 rounded-xl bg-purple-500/10 flex items-center justify-center mb-5 group-hover:bg-purple-500/20 transition-colors">
                  <feat.icon className="w-5 h-5 text-purple-400/70" />
                </div>
                <h3 className="text-base font-semibold mb-2 text-white/85">{feat.title}</h3>
                <p className="text-sm text-white/30 leading-relaxed">{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/* HOW IT WORKS                                       */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="py-28 px-6 relative">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-panel mb-6">
              <Network className="w-3.5 h-3.5 text-purple-400/60" />
              <span className="text-xs text-white/40 tracking-widest uppercase font-medium">Fluxo Operacional</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Como <span className="ella-command-gradient-text">Funciona</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", icon: BarChart3, title: "Gere o QR Code", desc: "Acesse o dashboard e gere um QR code único para a sessão de monitoramento." },
              { step: "02", icon: Smartphone, title: "Conecte o Dispositivo", desc: "Escaneie o QR code com o celular de campo. Autorize câmera, microfone e GPS." },
              { step: "03", icon: Eye, title: "Monitore em Tempo Real", desc: "Acompanhe vídeo, localização, status e eventos ao vivo no dashboard de comando." },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="text-center"
              >
                <div className="text-6xl font-black ella-command-gradient-text mb-5 opacity-30" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {item.step}
                </div>
                <div className="w-14 h-14 rounded-xl ella-command-gradient flex items-center justify-center mx-auto mb-5 ella-command-glow">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-white/85">{item.title}</h3>
                <p className="text-sm text-white/35 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/* EXPANSION ROADMAP                                  */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="py-28 px-6 relative">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent, rgba(124,58,237,0.02) 50%, transparent)" }} />
        <div className="relative max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-panel mb-6">
              <Radar className="w-3.5 h-3.5 text-purple-400/60" />
              <span className="text-xs text-white/40 tracking-widest uppercase font-medium">Roadmap</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Visão de <span className="ella-command-gradient-text">Expansão</span>
            </h2>
            <p className="text-white/35 max-w-xl mx-auto text-lg font-light">
              Arquitetura modular preparada para escalar conforme a operação cresce.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Fingerprint, label: "Reconhecimento Facial", status: "Ativo", active: true },
              { icon: Send, label: "Acionamento de Agentes", status: "Ativo", active: true },
              { icon: ShieldCheck, label: "Classificação de Eventos", status: "Ativo", active: true },
              { icon: Activity, label: "Timeline de Tratativas", status: "Ativo", active: true },
              { icon: Users, label: "Múltiplos Dispositivos", status: "Roadmap", active: false },
              { icon: Target, label: "Rondas Programadas", status: "Roadmap", active: false },
              { icon: FileText, label: "Captura de Evidências", status: "Roadmap", active: false },
              { icon: Bell, label: "Alertas Automáticos", status: "Roadmap", active: false },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="flex items-center gap-3 glass-panel rounded-xl p-4 hover:border-purple-500/15 transition-all"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${item.active ? "bg-purple-500/15" : "bg-white/[0.03]"}`}>
                  <item.icon className={`w-5 h-5 ${item.active ? "text-purple-400/70" : "text-white/20"}`} />
                </div>
                <div>
                  <div className="text-sm font-medium text-white/70">{item.label}</div>
                  <div className={`text-xs ${item.active ? "text-purple-400/60" : "text-white/20"}`}>{item.status}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/* CTA — Final                                        */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="py-28 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={0}
            className="glass-panel rounded-3xl p-14 relative overflow-hidden"
          >
            <div className="absolute inset-0 ella-command-gradient opacity-[0.04]" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl ella-command-gradient flex items-center justify-center mx-auto mb-8 ella-command-glow">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Pronto para o <span className="ella-command-gradient-text">comando</span>?
              </h2>
              <p className="text-white/35 mb-10 max-w-lg mx-auto text-lg font-light">
                Inicie uma demonstração ao vivo. Gere o QR code, conecte seu celular e veja o monitoramento em tempo real.
              </p>
              <Button
                size="lg"
                onClick={() => navigate("/dashboard")}
                className="ella-command-gradient text-white border-0 hover:opacity-90 text-base px-12 py-7 font-semibold ella-command-glow rounded-xl"
              >
                <Crosshair className="w-5 h-5 mr-2" />
                Acessar Plataforma
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/* FOOTER                                             */}
      {/* ═══════════════════════════════════════════════════ */}
      <footer className="py-10 px-6 border-t border-white/[0.04]">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg ella-command-gradient flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                ELLA <span className="text-purple-400/50 text-xs font-normal">by GAP</span>
              </span>
              <span className="text-[9px] text-white/20 tracking-[0.15em] uppercase">Command Platform</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/20">
            <Lock className="w-3 h-3" />
            Plataforma Inteligente de Segurança e Monitoramento em Tempo Real
          </div>
        </div>
      </footer>
    </div>
  );
}
